/// <amd-module name="typeahead/core/google/GoogleMapsDataProvider" />
import {GoogleRequestType, IEssResponse} from "typeahead/core/EssDataProvider";
import {AddressAutocompleteFailureMetric} from "typeahead/core/google/AddressAutocompleteFailureMetric";
import {POWERED_BY_GOOGLE_LOGO} from "typeahead/core/google/PoweredByGoogleLogo";
import {IDataProvider, IDataProviderResponse} from "typeahead/core/IDataProvider";
import {IListItem, LabelTypeListItem, ListItemType} from "typeahead/core/IListItem";
import {CollectorTransport} from "typeahead/core/logging/CollectorTransport";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {ICombinedStaticEssResponse} from "typeahead/core/StaticDataProvider";
import {ISuggestionResult, SuggestionCategory} from "typeahead/core/Suggestion";
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import PredictionSubstring = google.maps.places.PredictionSubstring;
import AutocompleteService = google.maps.places.AutocompleteService;
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

// Load and execute google maps api script
const locale: string = TypeaheadUtils.getLocale().replace("_", "-");

export interface IGoogleMapsDataProviderOptions {
    activeEssTests: string;
    domElementId: string;
    lob: string;
    requestType: string;
    srchText: string;
}

export interface IGoogleMapsSuggestionResult extends ISuggestionResult {
    sourceId: string;
    sourceName: string;
}

export interface IGoogleMapsDataProviderResponse extends IDataProviderResponse {
    query: string;
}

export interface ICombinedGoogleEssResponse extends IEssResponse, IGoogleMapsDataProviderResponse {}

export class GoogleMapsDataProvider implements IDataProvider {

    private static isGoogleMapsScriptAdded: boolean = false;
    private static readonly MAX_RESULTS: number = 5;

    private static getSuggestionResult(prediction: AutocompletePrediction, index): IGoogleMapsSuggestionResult {
        return  {
            category: SuggestionCategory[SuggestionCategory.GOOGLEMAPS],
            coordinates: undefined,
            index,
            listItemType: ListItemType.Suggestion,
            regionNames: {
                displayName: GoogleMapsDataProvider.boldMatchedSubStringInPrediction(prediction),
                fullName: prediction.description,
                lastSearchName : prediction.description,
            },
            sourceId: prediction.place_id,
            sourceName: "GPA",
            type : "ADDRESS",
        };
    }

    private static boldMatchedSubStringInPrediction(prediction) {
        const displayname: string = prediction.description;
        let result = "";
        let connectHead = 0;
        const matchedSubstrings: PredictionSubstring[] = prediction.matched_substrings;
        let matchedSubstring: PredictionSubstring;
        for (let index: number = 0; index < matchedSubstrings.length; index++) {
            matchedSubstring = matchedSubstrings[index];
            result += displayname.slice(connectHead, matchedSubstring.offset);
            result += "<B>" + displayname.slice(matchedSubstring.offset, matchedSubstring.offset +
                    matchedSubstring.length) + "</B>";
            connectHead = matchedSubstring.offset + matchedSubstring.length;
        }

        return result + displayname.slice(connectHead);
    }

    /**
     * Combine dataprovider results in the following order: ESS results precede Google results.
     */
    private static mergeResponsesFromGoogleEss(googleResponse: IGoogleMapsDataProviderResponse,
                                               essResponse: ICombinedStaticEssResponse): ICombinedGoogleEssResponse {
        let combinedResponse: ICombinedGoogleEssResponse = {
            activeABTests: essResponse.activeABTests,
            isReferralToGoogleMaps: essResponse.isReferralToGoogleMaps,
            metadata: essResponse.metadata,
            overriddenABTests: essResponse.overriddenABTests,
            query: essResponse.query,
            requestId: essResponse.requestId,
            responseCode: essResponse.responseCode,
            results: essResponse.results,
        };

        const essResultsLength: number = essResponse.results && essResponse.results.length > 0 ?
            essResponse.results.length : 0;
        combinedResponse =
            GoogleMapsDataProvider.updateCombinedResponseResults(combinedResponse, googleResponse, essResultsLength);

        return combinedResponse;
    }

    // Utility function to update the combined response results both indices and number
    private static updateCombinedResponseResults(combinedResponse: ICombinedGoogleEssResponse,
                                                 googleResponse: IGoogleMapsDataProviderResponse,
                                                 essLength: number): ICombinedGoogleEssResponse {
        let index: number = essLength;
        googleResponse.results.forEach( (result) => {
            if (index > 0) {
                // Type assertion to cast result as ISuggestionResult instead of IListItem
                (result as ISuggestionResult).index = (index++) + "";
            }

            combinedResponse.results = combinedResponse.results.concat(result);
        });
        return combinedResponse;
    }

    private googleAutocompleteService: AutocompleteService;

    constructor(isTest: boolean = false) {
        if (!GoogleMapsDataProvider.isGoogleMapsScriptAdded && !isTest) {
            $.getScript("https://maps.googleapis.com/maps/api/js?client=gme-expedia&channel=ess&libraries=places"
                + "&language=" + locale + "&key=AIzaSyCWOZQsnLi-ZUxhocNm3jjhjX9kX_qBpaU");
            GoogleMapsDataProvider.isGoogleMapsScriptAdded = true;
        }
    }

    public fetchData(options: IGoogleMapsDataProviderOptions, essResponse: ICombinedStaticEssResponse):
        JQueryPromise<ICombinedGoogleEssResponse> {

        const deferred: JQueryDeferred<ICombinedGoogleEssResponse> = $.Deferred();
        const request = {
            input: options.srchText,
            types: GoogleRequestType[GoogleRequestType.ADDRESS_AUTOCOMPLETE] === options.requestType ?
                ["geocode"] : undefined,
        };

        if (google && google.maps && google.maps.places && google.maps.places.AutocompleteService) {
            this.getGoogleAutocompleteService().getPlacePredictions(request,
                (predictions: AutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) => {
                    // Resolve with merged response
                    this.handlePredictionsResponse(predictions, status, options, essResponse, deferred);
                });
        } else {
            TypeaheadErrorLogger.log(
                new Error("Google maps script not loaded"), Severity.Excusable, ProblemOrigin.UniversalTypeahead);
        }

        return deferred.promise();
    }

    private getGoogleAutocompleteService(): AutocompleteService {
        if (!this.googleAutocompleteService) {
            this.googleAutocompleteService = new google.maps.places.AutocompleteService();
        }
        return this.googleAutocompleteService;
    }

    private handlePredictionsResponse(predictions: AutocompletePrediction[],
                                      status: google.maps.places.PlacesServiceStatus,
                                      options: IGoogleMapsDataProviderOptions,
                                      essResponse: IEssResponse,
                                      deferred: JQueryDeferred<ICombinedGoogleEssResponse>) {

        const suggestionResults: IListItem[] = [];

        if (status === google.maps.places.PlacesServiceStatus.OK) {
            // status = ZERO_RESULT is treated as a failure as we only log failures for now.
            // In failure metrics we will get to know how often ZERO_RESULT happens.

            if (predictions) {
                const suggestionCount: number = Math.min(GoogleMapsDataProvider.MAX_RESULTS, predictions.length);
                for (let i: number = 0; i < suggestionCount; i++) {
                    suggestionResults.push(GoogleMapsDataProvider.getSuggestionResult(predictions[i], i));
                }
            }
        } else {
            new CollectorTransport().log(new AddressAutocompleteFailureMetric(
                options.lob,
                options.domElementId,
                options.srchText,
                google.maps.places.PlacesServiceStatus[status],
                options.activeEssTests), "typeahead_client_metrics");
        }
        if (suggestionResults && suggestionResults.length) {
            suggestionResults.push(this.getPoweredByGoogleListItem());
        }

        const googleResponse: IGoogleMapsDataProviderResponse = {query: options.srchText, results: suggestionResults};

        // Merge the google response
        const mergedResponse: ICombinedGoogleEssResponse =
            GoogleMapsDataProvider.mergeResponsesFromGoogleEss(googleResponse, essResponse);

        deferred.resolve(mergedResponse);
    }

    private getPoweredByGoogleListItem(): LabelTypeListItem {
        const getHtml = () => {
            return "<div class='powered_by_google' style='text-align:right;position:relative;top:0.5em;'>" +
                "<img src='" + POWERED_BY_GOOGLE_LOGO + "'/></div>";
        };
        return new LabelTypeListItem(getHtml(), "GOOGLEMAPS");
    }
}
